import { create, show, useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";

import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import Box from "ds/components/Box";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import useMigrateStackVendor from "shared/Stack/useMigrateStackVendor";
import { StackVendor } from "types/generated";

import { REFETCH_STACK_AND_STACK_SETTINGS_QUERIES } from "../../../constants";

type StackSettingsTerragruntMigrationConfirmationModalProps = {
  stackId: string;
};

const StackSettingsTerragruntMigrationConfirmationModal = create(
  function StackSettingsTerragruntMigrationConfirmationModal({
    stackId,
  }: StackSettingsTerragruntMigrationConfirmationModalProps) {
    const modal = useModal();
    const { reportSuccess } = useTypedContext(FlashContext);

    const [migrateVendor, { loading }] = useMigrateStackVendor({
      refetchQueries: REFETCH_STACK_AND_STACK_SETTINGS_QUERIES,
    });

    const handleConfirm = useCallback(() => {
      migrateVendor(
        {
          id: stackId,
          targetVendor: StackVendor.Terragrunt,
          config: null,
        },
        (data) => {
          if (data?.stackMigrateVendor) {
            reportSuccess({ message: "Stack vendor was successfully migrated" });
            modal.hide();
          }
        }
      );
    }, [migrateVendor, modal, reportSuccess, stackId]);

    return (
      <ConfirmationModal
        title="Confirm Migration"
        confirmationButtonLabel="Migrate"
        confirmationButtonVariant="primary"
        onConfirm={handleConfirm}
        isLoading={loading}
        isDismissable={!loading}
      >
        <Box direction="column" gap="medium">
          <ConfirmationModalMessage>
            This will change the stack vendor from Terraform to Terragrunt. The stack will use
            native Terragrunt support instead of running Terragrunt through labels. Your existing
            configuration and state will be preserved.
          </ConfirmationModalMessage>
          <ConfirmationModalMessage>
            You will be able to roll the migration back.
          </ConfirmationModalMessage>
        </Box>
      </ConfirmationModal>
    );
  }
);

export const showStackSettingsTerragruntMigrationConfirmationModal = (
  props: StackSettingsTerragruntMigrationConfirmationModalProps
) => show(StackSettingsTerragruntMigrationConfirmationModal, props);
